import type { AdminUser } from "@/lib/api/admin-login-user";

const ADMIN_AUTH_SESSION_KEY = "api-portal.admin.auth.session";
/** Set on explicit logout; blocks restoring stale tab-scoped sessionStorage. */
const ADMIN_AUTH_LOGGED_OUT_KEY = "api-portal.admin.auth.logged-out";
const ADMIN_AUTH_SESSION_CHANGED_EVENT = "api-portal:admin-auth-session-changed";

let adminSessionSnapshot: AdminAuthSession | null = null;
let adminSessionRaw: string | null = null;

function notifyAdminSessionChanged(): void {
  window.dispatchEvent(new Event(ADMIN_AUTH_SESSION_CHANGED_EVENT));
}

function invalidateAdminSessionCache(): void {
  adminSessionRaw = null;
  adminSessionSnapshot = null;
}

function markAdminLoggedOut(): void {
  window.localStorage.setItem(ADMIN_AUTH_LOGGED_OUT_KEY, "1");
}

function clearAdminLoggedOutFlag(): void {
  window.localStorage.removeItem(ADMIN_AUTH_LOGGED_OUT_KEY);
}

function isAdminLoggedOut(): boolean {
  return window.localStorage.getItem(ADMIN_AUTH_LOGGED_OUT_KEY) === "1";
}

function applyCrossTabAdminAuthStorage(event: StorageEvent): void {
  if (event.key === ADMIN_AUTH_SESSION_KEY) {
    if (event.newValue === null) {
      window.sessionStorage.removeItem(ADMIN_AUTH_SESSION_KEY);
      invalidateAdminSessionCache();
      notifyAdminSessionChanged();
    } else {
      clearAdminLoggedOutFlag();
      invalidateAdminSessionCache();
      notifyAdminSessionChanged();
    }
    return;
  }

  if (event.key === ADMIN_AUTH_LOGGED_OUT_KEY && event.newValue === "1") {
    window.sessionStorage.removeItem(ADMIN_AUTH_SESSION_KEY);
    invalidateAdminSessionCache();
    notifyAdminSessionChanged();
  }
}

export interface AdminAuthSession {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
  user: AdminUser;
}

export type AdminAuthSessionPersist = "local" | "session";

export interface SetAdminAuthSessionOptions {
  persist?: AdminAuthSessionPersist;
}

function readAdminSessionRaw(): string | null {
  const localRaw = window.localStorage.getItem(ADMIN_AUTH_SESSION_KEY);
  if (localRaw) {
    return localRaw;
  }
  return window.sessionStorage.getItem(ADMIN_AUTH_SESSION_KEY);
}

function readAdminSessionSnapshot(): AdminAuthSession | null {
  if (isAdminLoggedOut()) {
    window.sessionStorage.removeItem(ADMIN_AUTH_SESSION_KEY);
    return null;
  }

  const raw = readAdminSessionRaw();
  if (raw === adminSessionRaw) {
    return adminSessionSnapshot;
  }

  adminSessionRaw = raw;
  if (!raw) {
    adminSessionSnapshot = null;
    return null;
  }

  try {
    adminSessionSnapshot = JSON.parse(raw) as AdminAuthSession;
  } catch {
    adminSessionSnapshot = null;
  }

  return adminSessionSnapshot;
}

function persistAdminSessionRaw(
  raw: string,
  persist: AdminAuthSessionPersist,
): void {
  if (persist === "local") {
    window.localStorage.setItem(ADMIN_AUTH_SESSION_KEY, raw);
    window.sessionStorage.removeItem(ADMIN_AUTH_SESSION_KEY);
  } else {
    window.sessionStorage.setItem(ADMIN_AUTH_SESSION_KEY, raw);
    window.localStorage.removeItem(ADMIN_AUTH_SESSION_KEY);
  }
  clearAdminLoggedOutFlag();
}

export function setAdminAuthSession(
  session: AdminAuthSession,
  options?: SetAdminAuthSessionOptions,
): void {
  const persist = options?.persist ?? "session";
  const raw = JSON.stringify(session);
  persistAdminSessionRaw(raw, persist);
  adminSessionSnapshot = session;
  adminSessionRaw = raw;
  notifyAdminSessionChanged();
}

export function getAdminAuthSession(): AdminAuthSession | null {
  return readAdminSessionSnapshot();
}

export function getAdminAuthSessionSnapshot(): AdminAuthSession | null {
  return readAdminSessionSnapshot();
}

export function clearAdminAuthSession(): void {
  window.localStorage.removeItem(ADMIN_AUTH_SESSION_KEY);
  window.sessionStorage.removeItem(ADMIN_AUTH_SESSION_KEY);
  markAdminLoggedOut();
  adminSessionSnapshot = null;
  adminSessionRaw = null;
  notifyAdminSessionChanged();
}

export function subscribeAdminAuthSession(onChange: () => void): () => void {
  const handler = () => onChange();
  window.addEventListener(ADMIN_AUTH_SESSION_CHANGED_EVENT, handler);
  return () => {
    window.removeEventListener(ADMIN_AUTH_SESSION_CHANGED_EVENT, handler);
  };
}

export function getAdminAccessToken(): string | null {
  return getAdminAuthSession()?.accessToken ?? null;
}

const crossTabStorageFlag = "__apiPortalAdminAuthStorageListening";
const windowWithAdminFlag = window as Window & {
  [crossTabStorageFlag]?: boolean;
};
if (!windowWithAdminFlag[crossTabStorageFlag]) {
  windowWithAdminFlag[crossTabStorageFlag] = true;
  window.addEventListener("storage", applyCrossTabAdminAuthStorage);
}
